import React, { useState, useEffect, useCallback } from 'react';
import { X, Save, Database } from 'lucide-react';
import { dataMappingAPI } from '../../services/api';
import toast from 'react-hot-toast';

interface AddDataSourceProviderModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const AddDataSourceProviderModal: React.FC<AddDataSourceProviderModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [formData, setFormData] = useState({
    data_source_id: '',
    data_sources_id: '',
    data_sources_description: '',
    data_source_contact_name: '',
    data_source_email: '',
    data_source_portal: '',
    project_name: '',
    deidentify_or_not: '',
    clinical_or_preclinical: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [draftSaved, setDraftSaved] = useState(false);
  const draftKey = 'data_source_provider_draft_v1';

  useEffect(() => {
    if (isOpen) {
      // Restore draft
      try {
        const saved = localStorage.getItem(draftKey);
        if (saved) {
          const parsed = JSON.parse(saved);
          setFormData(prev => ({
            ...prev,
            ...parsed,
          }));
        }
      } catch (error) {
        console.error('Error loading draft:', error);
      }
    }
  }, [isOpen]);

  // Function to save draft
  const saveDraft = useCallback(() => {
    try {
      // Only save if there's actual content
      const hasContent = formData.data_sources_id.trim() || 
                        formData.data_sources_description.trim() || 
                        formData.data_source_contact_name.trim() ||
                        formData.data_source_email.trim() ||
                        formData.data_source_portal.trim() ||
                        formData.project_name.trim();
      
      if (hasContent) {
        localStorage.setItem(draftKey, JSON.stringify(formData));
        setDraftSaved(true);
        // Hide the "draft saved" indicator after 2 seconds
        setTimeout(() => setDraftSaved(false), 2000);
      }
    } catch (error) {
      console.error('Error saving draft:', error);
    }
  }, [formData, draftKey]);

  // Autosave draft when formData changes (with debouncing)
  useEffect(() => {
    if (!isOpen) return;
    const timeoutId = setTimeout(saveDraft, 500); // Debounce for 500ms
    return () => clearTimeout(timeoutId);
  }, [formData, isOpen, saveDraft]);

  // Save draft when window loses focus
  useEffect(() => {
    const handleBeforeUnload = () => {
      saveDraft();
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        saveDraft();
      }
    };

    if (isOpen) {
      window.addEventListener('beforeunload', handleBeforeUnload);
      document.addEventListener('visibilitychange', handleVisibilityChange);
    }

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isOpen, saveDraft]);

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrors({});

    // Validation
    try {
      await dataMappingAPI.createDataSourceMapping(formData);
      
      toast.success('Data source provider mapping saved successfully');
      
      // Clear draft on success
      try { 
        localStorage.removeItem(draftKey); 
      } catch {}
      
      setFormData({
        data_source_id: '',
        data_sources_id: '',
        data_sources_description: '',
        data_source_contact_name: '',
        data_source_email: '',
        data_source_portal: '',
        project_name: '',
        deidentify_or_not: '',
        clinical_or_preclinical: '',
      });
      
      onSuccess();
      onClose();
    } catch (error: any) {
      const apiErrors = error?.response?.data?.errors as Array<{ msg: string; param?: string }> | undefined;
      if (apiErrors && apiErrors.length) {
        const fieldErrors: Record<string, string> = {};
        apiErrors.forEach(e => {
          if (e.param) fieldErrors[e.param] = e.msg;
        });
        setErrors(fieldErrors);
        toast.error('Please fix the highlighted fields.');
      } else {
        toast.error(error.response?.data?.error || 'Failed to save data source provider mapping');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <h2 className="text-2xl font-bold text-gray-900">Add Data Source Provider</h2>
            {draftSaved && (
              <div className="flex items-center space-x-1 text-green-600 text-sm">
                <Save className="h-4 w-4" />
                <span>Draft saved</span>
              </div>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Auto-save notice */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <div className="flex items-center space-x-2 text-blue-700 text-sm">
              <Save className="h-4 w-4" />
              <span>Your work is automatically saved as you type. You can safely navigate away and return later.</span>
            </div>
          </div>

          {/* Data_Sources_ID */}
          <div>
            <label htmlFor="data_sources_id" className="block text-sm font-medium text-gray-700 mb-2">
              Data_Sources_ID
            </label>
            <input
              type="text"
              id="data_sources_id"
              value={formData.data_sources_id}
              onChange={(e) => handleInputChange('data_sources_id', e.target.value)}
              placeholder="D001"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--accent)] focus:border-[var(--accent)]"
            />
            <p className="mt-1 text-xs text-gray-500">
              Unique identifier (D + 3 digits, e.g., D001, D002). Leave empty to auto-generate.
            </p>
          </div>

          {/* Data_Sources_Description */}
          <div>
            <label htmlFor="data_sources_description" className="block text-sm font-medium text-gray-700 mb-2">
              Data_Sources_Description
            </label>
            <input
              type="text"
              id="data_sources_description"
              value={formData.data_sources_description}
              onChange={(e) => handleInputChange('data_sources_description', e.target.value)}
              placeholder="UCSF | Savic Lab"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--accent)] focus:border-[var(--accent)]"
            />
            <p className="mt-1 text-xs text-gray-500">
              Use &quot;Institution | Team/Lab&quot; format.
            </p>
          </div>

          {/* Contact Name and Email */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="data_source_contact_name" className="block text-sm font-medium text-gray-700 mb-2">
                Data_Source_Contact_Name
              </label>
              <input
                type="text"
                id="data_source_contact_name"
                value={formData.data_source_contact_name}
                onChange={(e) => handleInputChange('data_source_contact_name', e.target.value)}
                placeholder="John Smith"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--accent)] focus:border-[var(--accent)]"
              />
            </div>
            <div>
              <label htmlFor="data_source_email" className="block text-sm font-medium text-gray-700 mb-2">
                Data_Source_email
              </label>
              <input
                type="email"
                id="data_source_email"
                value={formData.data_source_email}
                onChange={(e) => handleInputChange('data_source_email', e.target.value)}
                placeholder="name@institution.edu"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--accent)] focus:border-[var(--accent)]"
              />
            </div>
          </div>

          {/* Portal and Clinical Type */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="data_source_portal" className="block text-sm font-medium text-gray-700 mb-2">
                Data_Source_Portal
              </label>
              <input
                type="url"
                id="data_source_portal"
                value={formData.data_source_portal}
                onChange={(e) => handleInputChange('data_source_portal', e.target.value)}
                placeholder="https://portal.institution.edu"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--accent)] focus:border-[var(--accent)]"
              />
            </div>
            <div>
              <label htmlFor="clinical_or_preclinical" className="block text-sm font-medium text-gray-700 mb-2">
                Clinical_or_Preclinical
              </label>
              <select
                id="clinical_or_preclinical"
                value={formData.clinical_or_preclinical}
                onChange={(e) => handleInputChange('clinical_or_preclinical', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--accent)] focus:border-[var(--accent)]"
              >
                <option value="">Select...</option>
                <option value="Clinical">Clinical</option>
                <option value="Preclinical">Preclinical</option>
                <option value="Both">Both</option>
              </select>
            </div>
          </div>

          {/* Deidentify and Project Name */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="deidentify_or_not" className="block text-sm font-medium text-gray-700 mb-2">
                Deidentify_or_not
              </label>
              <select
                id="deidentify_or_not"
                value={formData.deidentify_or_not}
                onChange={(e) => handleInputChange('deidentify_or_not', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--accent)] focus:border-[var(--accent)]"
              >
                <option value="">Select...</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
            </div>
            <div>
              <label htmlFor="project_name" className="block text-sm font-medium text-gray-700 mb-2">
                Project_Name
              </label>
              <input
                type="text"
                id="project_name"
                value={formData.project_name}
                onChange={(e) => handleInputChange('project_name', e.target.value)}
                placeholder="COVID-19 Drug Response Study"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--accent)] focus:border-[var(--accent)]"
              />
              <p className="mt-1 text-xs text-gray-500">
                May contain multiple projects separated by semicolons.
              </p>
            </div>
          </div>

          {/* Submit buttons */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={() => {
                onClose();
                // Clear draft when explicitly closing
                try { localStorage.removeItem(draftKey); } catch {}
              }}
              className="px-4 py-2 rounded-lg border border-gray-300 text-sm text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2 rounded-lg bg-[var(--accent)] text-white text-sm font-medium hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Saving...' : 'Save Provider'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddDataSourceProviderModal;
